import type { BeehiveSite, UserResponse } from '@/auth/type';

export type PendoVisitorPayload = Record<string, string | boolean>;

export type PendoAccountPayload = Record<string, string | null | undefined>;

export type PendoIdentityPayload = {
  signature: string;
  visitor: PendoVisitorPayload;
  account?: PendoAccountPayload;
};

export function resolveBeehiveSiteForPendo(
  user: UserResponse,
  explorerSiteId?: string,
  explorerSiteName?: string
): BeehiveSite | null {
  if (explorerSiteId && explorerSiteName && user.authorized_sites?.length) {
    const match = user.authorized_sites.find(
      (s) => s.site_id === explorerSiteId && s.site_name === explorerSiteName
    );
    if (match) {
      return match;
    }
  }
  return user.active_site ?? null;
}

function buildVisitor(user: UserResponse): PendoVisitorPayload {
  const email = user.email;
  return {
    id: email,
    email,
    full_name: user.username,
    is_chargebee_user: email.toLowerCase().endsWith('@chargebee.com'),
  };
}

function buildAccount(site: BeehiveSite): PendoAccountPayload {
  const account: PendoAccountPayload = {
    id: site.site_id,
    domain: site.site_name,
    site_type: site.site_type,
  };
  if (site.merchant_id) {
    account.merchant_id = site.merchant_id;
  }
  if (site.site_status) {
    account.site_status = site.site_status;
  }
  if (site.site_plan_id) {
    account.site_plan_id = site.site_plan_id;
  }
  if (site.role != null && site.role !== '') {
    account.role = site.role;
  }
  return account;
}

export function buildPendoIdentityPayload(
  user: UserResponse | null | undefined,
  explorerSiteId?: string,
  explorerSiteName?: string
): PendoIdentityPayload {
  if (!user?.email) {
    return {
      signature: 'anonymous',
      visitor: { id: 'ANONYMOUS' },
    };
  }

  const site = resolveBeehiveSiteForPendo(
    user,
    explorerSiteId,
    explorerSiteName
  );

  if (!site) {
    return {
      signature: `${user.email}|`,
      visitor: buildVisitor(user),
    };
  }

  return {
    signature: `${user.email}|${site.site_id}`,
    visitor: buildVisitor(user),
    account: buildAccount(site),
  };
}
